/*
 * Project: bellcloud
 * File: apps/frontend/src/connections/modes/BurstMode.ts
 * Description: 버스트 모드 통신 (로그 다운로드용)
 */

import type {
  CommunicationMode,
  CommunicationContext,
  CommunicationResult,
  DeviceAdapter,
  PacketApiAdapter,
} from './types';
import type { PacketToJsonResponse } from '@packages/types/device/common/api.common';
import { FILE_TYPE_TO_FUNCTION_ID } from '@packages/types/device/lite/file_type_to_function_id';
import { MINI_FILE_TYPE_TO_FUNCTION_ID } from '@packages/types/device/mini/file_type_to_function_id';
import { SOF } from '@packages/protocols/constants';

/**
 * 패킷에서 payload 추출
 */
const getPayloadFromPacket = (packet: Uint8Array): Uint8Array => {
  const sofIndex = packet.indexOf(SOF);
  if (sofIndex < 0 || sofIndex + 8 > packet.length) {
    throw new Error('유효하지 않은 패킷: SOF 없음 또는 헤더 길이 부족');
  }

  const lenL = packet[sofIndex + 6];
  const lenH = packet[sofIndex + 7];
  const payloadLen = lenL | (lenH << 8);
  const payloadStart = sofIndex + 8;
  const payloadEnd = payloadStart + payloadLen;

  if (payloadEnd > packet.length) {
    throw new Error(
      `유효하지 않은 패킷: payload 길이 초과 (payloadEnd=${payloadEnd}, packetLen=${packet.length})`
    );
  }

  return packet.slice(payloadStart, payloadEnd);
};

/**
 * 스트림 청크 파싱 (로컬)
 */
const parseStreamChunk = (
  packetData: Uint8Array,
  fileType: number,
  isMiniDevice: boolean
): { chunk_index: number; chunk_data: number[] } & Record<string, number> => {
  const payload = getPayloadFromPacket(packetData);
  if (payload.length < 2) {
    throw new Error(`청크 payload 길이 부족: ${payload.length}`);
  }

  const chunkIndex = payload[0] | (payload[1] << 8);
  const chunkData = Array.from(payload.slice(2));

  const chunkJson: { chunk_index: number; chunk_data: number[] } & Record<string, any> = {
    chunk_index: chunkIndex,
    chunk_data: chunkData,
  };

  if (isMiniDevice) {
    if (fileType === 0) {
      chunkJson.datalog_chunk_index = chunkIndex;
    } else if (fileType === 1) {
      chunkJson.eventlog_chunk_index = chunkIndex;
    }
  }

  return chunkJson;
};

/**
 * 헤더 payload 파싱
 */
const readU16LE = (payload: Uint8Array, offset: number): number =>
  payload[offset] | (payload[offset + 1] << 8);

const readU32LE = (payload: Uint8Array, offset: number): number =>
  (payload[offset] |
    (payload[offset + 1] << 8) |
    (payload[offset + 2] << 16) |
    (payload[offset + 3] << 24)) >>>
  0;

const parseHeaderPayload = (payload: Uint8Array) => {
  if (payload.length < 12) {
    throw new Error(`헤더 payload 길이 부족: ${payload.length}`);
  }

  return {
    file_type: readU16LE(payload, 0),
    version: readU16LE(payload, 2),
    chunk_size: readU16LE(payload, 4),
    num_packets: readU16LE(payload, 6),
    total_size: readU32LE(payload, 8),
  };
};

/**
 * 버스트 모드 통신
 * 
 * 사용 케이스:
 * - 로그 다운로드 (Class 16)
 * - 대량 데이터 수신
 * 
 * 통신 흐름:
 * 1. 단일 패킷 모드로 헤더 수신
 * 2. 스트리밍 모드로 전환
 * 3. N개의 데이터 패킷 수신 (버스트)
 * 4. 단일 패킷 모드로 복귀
 */
export class BurstMode implements CommunicationMode {
  readonly name = 'burst' as const;
  private packetApi: PacketApiAdapter;
  private savePacketCallback?: (serialNumber: string, packet: number[]) => void;

  constructor(
    packetApi: PacketApiAdapter,
    savePacketCallback?: (serialNumber: string, packet: number[]) => void
  ) {
    this.packetApi = packetApi;
    this.savePacketCallback = savePacketCallback;
  }

  async execute(
    adapter: DeviceAdapter,
    context: CommunicationContext
  ): Promise<CommunicationResult> {
    const { deviceInfo, dto, isMiniDevice, fileType, isLogDownloadMode, onProgress } = context;

    try {
      // 1. 단일 패킷 모드로 헤더 수신
      adapter.setSinglePacketMode();

      // 2. 버퍼 클리어
      try {
        adapter.clearBuffer(false);
      } catch (error) {
        if (error instanceof Error && error.message.includes('버스트 모드')) {
          console.warn(`⚠️ [BurstMode] 버스트 모드 중 clearBuffer 차단됨`);
        } else {
          throw error;
        }
      }

      // 3. 패킷 생성
      const packetResponse = await this.packetApi.generatePacket({
        ...dto,
        deviceInfo: deviceInfo || undefined,
      });

      if (!packetResponse || !Array.isArray(packetResponse.packet)) {
        throw new Error('패킷 생성 실패');
      }

      const packetBytes = Uint8Array.from(packetResponse.packet);

      // 4–5. BLE/Serial: HTTP POST(단일)로 요청·응답.
      await adapter.write(packetBytes);
      const headerPacket: Uint8Array = await adapter.read();

      // 6. 헤더 패킷에서 class_id, function_id 추출
      const actualClassId = headerPacket[4];
      const actualFunctionId = headerPacket[5];

      // 7. 헤더 파싱
      let jsonResponse: PacketToJsonResponse;
      
      if (isLogDownloadMode) {
        // 로그 다운로드 모드: 로컬 파싱
        jsonResponse = {
          json: parseHeaderPayload(getPayloadFromPacket(headerPacket)),
        };
      } else {
        // 일반 모드: 백엔드 파싱
        jsonResponse = await this.packetApi.parsePacket({
          class_id: actualClassId,
          function_id: actualFunctionId,
          packet: Array.from(headerPacket),
          deviceInfo: deviceInfo || undefined,
        });
      }

      const firstResponse = jsonResponse.json as any;
      const numPackets = firstResponse.num_packets;

      // 8. Class 16이 아니거나 num_packets가 없으면 단일 패킷으로 처리
      if (!numPackets || typeof numPackets !== 'number' || numPackets <= 0) {
        // 패킷 저장
        if (deviceInfo?.serial_number && this.savePacketCallback) {
          this.savePacketCallback(deviceInfo.serial_number, Array.from(headerPacket));
        }

        return {
          success: true,
          response: jsonResponse,
          headerPacket,
        };
      }

      // 9. 스트리밍 모드로 전환 (버스트 수신)
      adapter.setStreamingMode();
      
      const startTime = performance.now();
      const allChunks: any[] = [];
      const shouldParseLocally = isLogDownloadMode && dto.function_id === 1;

      try {
        // 10. N개의 데이터 패킷 수신
        for (let i = 0; i < numPackets; i++) {
          const packetData = await adapter.readStreamPacket();

          // fileType 검증
          if (typeof fileType !== 'number') {
            throw new Error('fileType 정보가 없습니다. (청크 요청)');
          }

          if (shouldParseLocally) {
            // 로컬 파싱
            const chunkJson = parseStreamChunk(packetData, fileType, isMiniDevice);
            allChunks.push(chunkJson);
          } else {
            // 백엔드 파싱
            let functionId: number;

            if (isMiniDevice) {
              // MINI: file_type=0 (Datalog)일 때만 0x18 사용
              if (fileType === 0) {
                functionId = 0x18; // MINI_BURST_RESPONSE_FUNCTION_ID
              } else {
                const miniMapping = MINI_FILE_TYPE_TO_FUNCTION_ID as Record<number, number>;
                functionId = miniMapping[fileType] ?? 0;
              }
            } else {
              // LITE
              const liteMapping = FILE_TYPE_TO_FUNCTION_ID as Record<number, number>;
              functionId = liteMapping[fileType] ?? 0;
            }

            const parsed = await this.packetApi.parsePacket({
              class_id: dto.class_id,
              function_id: functionId,
              packet: Array.from(packetData),
              deviceInfo: deviceInfo || undefined,
            });

            if (parsed && 'json' in parsed) {
              allChunks.push(parsed.json);
            }
          }

          // 진행률 콜백
          if (onProgress) {
            const progressValue = Math.round(((i + 1) / numPackets) * 1000) / 10;
            onProgress(progressValue, i + 1, numPackets);
          }
        }

        // 11. 모든 데이터 패킷을 첫 번째 응답에 병합
        jsonResponse.json = {
          ...jsonResponse.json,
          dataPackets: allChunks,
        };
      } catch (error) {
        const elapsed = performance.now() - startTime;
        console.error(
          `❌ [BurstMode] 패킷 수신 중 오류 (${(elapsed / 1000).toFixed(1)}초):`,
          error
        );

        // 부분적으로 수신된 패킷이라도 반환
        if (allChunks.length > 0) {
          jsonResponse.json = {
            ...jsonResponse.json,
            dataPackets: allChunks,
            incomplete: true,
            receivedPackets: allChunks.length,
          };
        } else {
          throw error;
        }
      } finally {
        // 12. 스트리밍 모드 종료 (항상 실행)
        adapter.setSinglePacketMode();
      }

      // 13. 패킷 저장
      if (deviceInfo?.serial_number && this.savePacketCallback) {
        this.savePacketCallback(deviceInfo.serial_number, Array.from(headerPacket));
      }

      return {
        success: true,
        response: jsonResponse,
        headerPacket,
      };
    } catch (error) {
      // 에러 발생 시에도 모드 복귀
      adapter.setSinglePacketMode();

      return {
        success: false,
        response: { json: {} },
        error: error instanceof Error ? error : new Error(String(error)),
      };
    }
  }
}
